// Native replacement for dashboard/src/components/SignupPage.ts — see
// LoginPage.ts's header comment for why this exists. Ported behavior-for-
// behavior: same plan picker, same fields, same validation-error mapping.
// Where it lands after a successful signup is NOT this page's call — the
// server decides (result.redirect, apiSignup.ts) and today that's still
// '/dashboard' (self-service-signup.md §A.1/§A.10's "configure before pay"
// preview) — porting this form doesn't require that destination to be
// native too; it just follows wherever the server sends it, unchanged.
import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';

type Plan = 'concierge';
type Step = 'plan' | 'form' | 'submitting';

interface FormState {
  businessName: string;
  ownerName: string;
  email: string;
  password: string;
  timezone: string;
  businessPhone: string;
}

const TIMEZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu'];
const DEFAULT_TIMEZONE = 'America/New_York';

function detectTimezone(): string {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TIMEZONES.includes(detected) ? detected : DEFAULT_TIMEZONE;
}

function planFromQuery(): Plan | null {
  const planParam = new URLSearchParams(window.location.search).get('plan');
  return planParam === 'concierge' ? planParam : null;
}

export function SignupPage() {
  // Both `window.location` (query string) and the visitor's real timezone
  // are client-only signals — SSR has neither the request's browser URL nor
  // the visitor's Intl zone (only the server's own, which would silently
  // mismatch the client's). Render the same generic, deterministic default
  // for every SSR pass (plan picker, America/New_York) and correct it once
  // mounted, the same discipline Shell.ts's own header comment describes.
  const [step, setStep] = useState<Step>('plan');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [form, setForm] = useState<FormState>({
    businessName: '', ownerName: '', email: '', password: '',
    timezone: DEFAULT_TIMEZONE, businessPhone: '',
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const queryPlan = planFromQuery();
    if (queryPlan) { setPlan(queryPlan); setStep('form'); }
    setForm(f => ({ ...f, timezone: detectTimezone() }));
  }, []);

  function selectPlan(p: Plan): void {
    setPlan(p);
    setStep('form');
    setError('');
    setFieldErrors({});
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm(f => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    if (!plan) return;
    setStep('submitting');
    setError('');
    setFieldErrors({});

    try {
      const res = await fetch('/api/signup/start', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan, businessName: form.businessName, ownerName: form.ownerName,
          email: form.email, password: form.password, timezone: form.timezone,
          businessPhone: form.businessPhone || undefined,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw body;

      // Full-page navigation (not a soft nav): the server-decided destination
      // (today, '/dashboard') needs a fresh session load, the same reason
      // dashboard/'s own SignupPage.ts did this.
      window.location.href = body.redirect;
    } catch (err) {
      const e = err as { error?: string };
      if (e?.error === 'email_taken') {
        setStep('form');
        setFieldErrors({ email: 'An account with this email already exists. Try logging in.' });
      } else if (e?.error === 'validation') {
        setStep('form');
        setError('Please correct the errors below.');
      } else {
        setStep('form');
        setError(e?.error || 'Something went wrong. Please try again.');
      }
    }
  }

  function planCard(p: Plan, title: string, price: string, features: string[], cta: string, note?: string) {
    const selected = plan === p;
    return h('div', {
      class: 'auth-plan-card',
      style: selected ? 'border-color:var(--accent);box-shadow:0 0 0 1px var(--accent)' : '',
      onClick: () => selectPlan(p),
    },
      h('div', { class: 'auth-plan-title' }, title),
      h('div', { class: 'auth-plan-price' }, price, h('span', { class: 'auth-plan-price-period' }, '/mo')),
      h('ul', { class: 'auth-plan-features' }, ...features.map(f => h('li', null, f))),
      note ? h('p', { class: 'auth-plan-note' }, note) : null,
      h('span', { class: 'auth-submit', style: 'display:block;text-align:center;pointer-events:none;' }, cta),
    );
  }

  function field(label: string, key: keyof FormState, type = 'text', placeholder = '') {
    const err = fieldErrors[key];
    return h('div', { class: 'auth-field' },
      h('label', null, label),
      h('input', {
        type, value: form[key], placeholder,
        onInput: (e: Event) => setField(key, (e.target as HTMLInputElement).value),
      }),
      err ? h('p', { class: 'auth-field-error' }, err) : null,
    );
  }

  if (step === 'plan') {
    return h('div', { class: 'auth-plans' },
      h('h1', { class: 'auth-plans-title' }, 'Get started with Oakleigh'),
      h('p', { class: 'auth-plans-sub' },
        "Free to create. Buy a single job with no account, or set up Concierge and activate it whenever you're ready.",
      ),
      h('div', { class: 'auth-plans-row' },
        h('a', { href: 'https://oakcityintelligence.com/#products', class: 'auth-plan-card', style: 'display:block;text-decoration:none;color:inherit;' },
          h('div', { class: 'auth-plan-title' }, 'Pay per job'),
          h('div', { class: 'auth-plan-price' }, 'From $1'),
          h('ul', { class: 'auth-plan-features' },
            h('li', null, 'Lead generation, articles, video, and more'),
            h('li', null, 'No account or subscription required'),
            h('li', null, "Delivered by email when it's done"),
          ),
          h('p', { class: 'auth-plan-note' }, 'Turn any job into a subscription later'),
          h('span', { class: 'auth-submit', style: 'display:block;text-align:center;' }, "See what's available →"),
        ),
        planCard('concierge', 'Concierge', '$149', [
          'Responds to calls, texts, and messages',
          'Answers FAQs and captures leads',
          'Available 24/7',
          'Dashboard and reporting',
        ], 'Create your account', "Free to set up — you activate billing when you're ready"),
      ),
      h('p', { class: 'auth-plans-signin' }, 'Already have an account? ', h('a', { href: '/login' }, 'Sign in')),
    );
  }

  if (step === 'submitting') {
    return h('div', { class: 'auth-screen' }, h('p', { class: 'auth-plans-sub' }, 'Setting up your account…'));
  }

  const planLabel = 'Concierge ($149/mo)';
  return h('div', { class: 'auth-screen' },
    h('div', { class: 'auth-card', style: 'max-width:440px;' },
      h('div', { class: 'auth-form-header' },
        h('button', { class: 'auth-back', onClick: () => { setStep('plan'); setError(''); setFieldErrors({}); } }, '←'),
        h('div', null,
          h('h1', { class: 'auth-title', style: 'margin:0;' }, 'Create your account'),
          h('p', { class: 'auth-plans-sub', style: 'margin:0;' }, planLabel),
        ),
      ),
      error ? h('p', { class: 'auth-error' }, error) : null,
      h('form', { onSubmit: handleSubmit },
        field('Business name', 'businessName', 'text', 'Acme HVAC'),
        field('Your name', 'ownerName', 'text', 'Jane Smith'),
        field('Email', 'email', 'email', 'jane@acmehvac.com'),
        field('Password', 'password', 'password'),
        h('div', { class: 'auth-field' },
          h('label', null, 'Timezone'),
          h('select', {
            value: form.timezone,
            onChange: (e: Event) => setField('timezone', (e.target as HTMLSelectElement).value),
          },
            ...TIMEZONES.map(tz => h('option', { value: tz }, tz.replace('America/', '').replace('_', ' '))),
          ),
        ),
        field('Business phone (optional)', 'businessPhone', 'tel', '+1 555 000 0000'),
        h('button', { type: 'submit', class: 'auth-submit', style: 'margin-top:var(--space-2);' }, 'Create account →'),
      ),
      h('p', { class: 'auth-plans-sub', style: 'font-size:var(--text-xs);margin-top:var(--space-4);text-align:center;' },
        'By signing up you agree to our ',
        h('a', { href: 'https://oakcityintelligence.com/terms/', rel: 'noopener' }, 'Terms of Service'),
        ' and ',
        h('a', { href: 'https://oakcityintelligence.com/privacy/', rel: 'noopener' }, 'Privacy Policy'),
        '.',
      ),
    ),
  );
}
